import { Link, useLocation, useParams } from 'react-router';
import type { Renewal } from '@bekebi/contracts/api';
import { ApiError } from '../../api/client';
import { ErrorState, LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { isServiceId, useServices } from '../services/api';
import { DefinitionList, Section, UnavailableNote } from '../services/components';
import { formatDate, formatDateTime } from '../services/format';
import { subjectPath, useRenewal, useRenewalHistory, useSubjectRenewals } from './api';
import { HistoryEntry, RenewalStateBadge, formatMoney } from './present';
import { EditRenewalForm, RenewForm } from './renewal-forms';

/**
 * One renewal term (/app/renewals/:renewalId): its dates, price and the API's condition and
 * countdown; editing and renewing while it is open (renewals.manage); every term of the same
 * service; and this term's history, exactly as recorded (append-only).
 */
export function RenewalDetail() {
  const { renewalId } = useParams();
  if (!isServiceId(renewalId)) return <RenewalNotFound />;
  return <RenewalDetailFor id={renewalId} />;
}

function RenewalNotFound() {
  return (
    <div className="max-w-3xl">
      <h1>Renewal term not found</h1>
      <p className="mt-2 text-sm text-muted">There is no renewal term at this address.</p>
      <p className="mt-4 text-sm">
        <Link
          to="/app/renewals"
          className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
        >
          Back to Renewals
        </Link>
      </p>
    </div>
  );
}

function RenewalDetailFor({ id }: { id: string }) {
  const renewal = useRenewal(id);
  if (renewal.isPending) return <LoadingState label="Loading renewal term…" />;
  if (renewal.isError) {
    if (renewal.error instanceof ApiError && renewal.error.status === 404)
      return <RenewalNotFound />;
    return (
      <ErrorState
        title="The renewal term could not be loaded"
        message={renewal.error.message}
        onRetry={() => void renewal.refetch()}
      />
    );
  }
  return <RenewalView renewal={renewal.data} />;
}

function RenewalView({ renewal }: { renewal: Renewal }) {
  const canManage = useHasPermission('renewals.manage');
  const services = useServices();
  const service =
    renewal.serviceId !== null ? services.data?.find((s) => s.id === renewal.serviceId) : undefined;
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice;
  const serviceName = service?.name ?? renewal.subjectName;
  const path = subjectPath(renewal);

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <p className="text-sm">
          <Link
            to="/app/renewals"
            className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
          >
            Renewals
          </Link>
        </p>
        <h1 className="mt-2">{serviceName} renewal term</h1>
        <p className="mt-1 text-sm text-muted">
          {renewal.serviceId === null ? 'Infrastructure' : 'Service'}:{' '}
          {renewal.serviceId === null ? (
            renewal.subjectName
          ) : service ? (
            <Link
              to={`/app/services/${service.id}`}
              className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
            >
              {service.name}
            </Link>
          ) : (
            'not loaded'
          )}
        </p>
        {notice ? (
          <p role="status" className="mt-4 rounded-md bg-success-soft p-3 text-sm text-success">
            {notice}
          </p>
        ) : null}
      </div>

      <Section title="Term">
        <div className="mb-4">
          <RenewalStateBadge renewal={renewal} />
        </div>
        <DefinitionList
          items={[
            ['State', renewal.state === 'open' ? 'Open' : 'Closed'],
            ['Term start', formatDate(renewal.termStart)],
            ['Renewal date', formatDate(renewal.renewalDate)],
            ['Price', formatMoney(renewal.price.amount, renewal.price.currency)],
            ...(renewal.closedAt
              ? ([['Closed at', formatDateTime(renewal.closedAt)]] as [string, string][])
              : []),
            ['Recorded', formatDateTime(renewal.createdAt)],
            ['Last changed', formatDateTime(renewal.updatedAt)],
          ]}
        />
        {renewal.state === 'closed' ? (
          <p className="mt-4 text-sm text-muted">
            Closed terms are kept as history and cannot be changed.
          </p>
        ) : null}
      </Section>

      {canManage && renewal.state === 'open' ? (
        <>
          <Section
            title="Renew"
            description="Record the next term: this term is closed and a new open term starts."
          >
            <RenewForm renewal={renewal} />
          </Section>
          <Section title="Edit" description="Correct this open term's dates or price.">
            <EditRenewalForm renewal={renewal} />
          </Section>
        </>
      ) : null}

      <TermsSection path={path} currentId={renewal.id} />
      <HistorySection path={path} renewalId={renewal.id} />
    </div>
  );
}

function TermsSection({ path, currentId }: { path: string; currentId: string }) {
  const terms = useSubjectRenewals(path);
  return (
    <Section title="All terms">
      {terms.isPending ? (
        <LoadingState label="Loading terms…" />
      ) : terms.isError ? (
        <ErrorState
          title="Terms could not be loaded"
          message={terms.error.message}
          onRetry={() => void terms.refetch()}
        />
      ) : (
        <ul aria-label="All terms" className="divide-y divide-line">
          {terms.data.map((term) => (
            <li
              key={term.id}
              className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm"
            >
              {term.id === currentId ? (
                <span className="font-semibold text-fg" aria-current="page">
                  {formatDate(term.termStart)} – {formatDate(term.renewalDate)} (this term)
                </span>
              ) : (
                <Link
                  to={`/app/renewals/${term.id}`}
                  className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
                >
                  {formatDate(term.termStart)} – {formatDate(term.renewalDate)}
                </Link>
              )}
              <RenewalStateBadge renewal={term} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function HistorySection({ path, renewalId }: { path: string; renewalId: string }) {
  const history = useRenewalHistory(path);
  const entries = (history.data ?? []).filter((e) => e.renewalId === renewalId);
  return (
    <Section
      title="History"
      description="What happened to this term, as recorded. Entries are never changed."
    >
      {history.isPending ? (
        <LoadingState label="Loading history…" />
      ) : history.isError ? (
        <ErrorState
          title="History could not be loaded"
          message={history.error.message}
          onRetry={() => void history.refetch()}
        />
      ) : entries.length === 0 ? (
        <UnavailableNote>No history is recorded for this term.</UnavailableNote>
      ) : (
        <ol aria-label="History" className="space-y-3">
          {entries.map((entry) => (
            <li key={entry.id}>
              <HistoryEntry entry={entry} />
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}
